/**
 * useNoteSectionCopy - Hook for copying individual note sections
 *
 * Handles:
 * - Copying a single section to clipboard
 * - Building HTML and plain text representations
 * - Applying user copy/paste preferences
 * - Clipboard operations with rich text support
 * - Success/error notifications
 * - Event tracking (Statsig + new telemetry system)
 * - Visual state management (idle/copying/success/error)
 *
 * Simpler than useNoteCopy - designed for single-section quick copy
 */

import { useEffect, useRef, useState } from 'react';
import type { NoteSection } from '@features/notes';
import { useUserPreferences } from '@features/user-preferences';
import { useEventTracking } from '@services/statsig';
import { buildSelectionCopied, type TelemetryContext } from '@utils/analytics/noteTelemetryBuilder';
import type { AnyBlockNoteEditor } from '@utils/blocknote/types';
import { copyRichText } from '@utils/utils';
import { marked } from 'marked';
import { useSnackbar } from 'notistack';
import removeMd from 'remove-markdown';

/**
 * Copy/Paste Preferences Interface
 * Temporary type until backend types are updated
 */
interface CopyPastePreferences {
  include_header_on_copy_multiple?: boolean;
  html_formatting_on_copy?: boolean;
}

interface UserPreferencesWithCopy {
  copy_paste_preferences?: CopyPastePreferences;
}

export type CopyState = 'idle' | 'copying' | 'success' | 'error';

export interface UseNoteSectionCopyReturn {
  /** Copy a single section to clipboard */
  copySection: (
    section: NoteSection,
    noteId?: string,
    encounterId?: string,
    pendingContent?: string,
    blockNoteEditor?: AnyBlockNoteEditor,
  ) => Promise<void>;

  /** Current copy state for visual feedback */
  copyState: CopyState;
}

/**
 * useNoteSectionCopy hook
 *
 * Usage:
 * ```tsx
 * const { copySection, copyState } = useNoteSectionCopy();
 *
 * <Button onClick={() => copySection(section, noteId, encounterId)}>
 *   {copyState === 'copying' ? 'Copying...' : 'Copy'}
 * </Button>
 * ```
 */
export function useNoteSectionCopy(): UseNoteSectionCopyReturn {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const { preferences } = useUserPreferences();
  const { noteSection: noteSectionMetrics } = useEventTracking();

  // Cleanup function to clear any pending timeout
  const clearStateTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Cleanup on unmount to prevent setState on unmounted component
  useEffect(() => {
    return () => {
      clearStateTimeout();
    };
  }, []);

  /**
   * Copy a single section to clipboard
   * @param pendingContent - Optional pending AI-generated content to use instead of section.edited_text
   * @param blockNoteEditor - Optional BlockNote editor instance for better copy fidelity
   */
  const copySection = async (
    section: NoteSection,
    noteId?: string,
    encounterId?: string,
    pendingContent?: string,
    blockNoteEditor?: AnyBlockNoteEditor,
  ) => {
    if (!section) {
      enqueueSnackbar('No section available to copy', { variant: 'error' });
      return;
    }

    // Track interaction event (user clicked Copy button)
    noteSectionMetrics.copyClicked({
      section_id: section.section_id,
      section_type: section.name,
      section_name: section.name,
      note_id: noteId,
      encounter_id: encounterId,
    });

    setCopyState('copying');

    try {
      let htmlString = '';
      let plainTextString = '';

      // Get user preferences for copying
      const prefs = preferences as UserPreferencesWithCopy;
      const includeHtml = prefs?.copy_paste_preferences?.html_formatting_on_copy ?? true;

      // Use pending content if provided (for auto-approve flow), otherwise use section content
      const currentText = pendingContent || section.edited_text || section.text;

      if (!currentText) {
        plainTextString += '\n\n';
        htmlString += '<br/>';
      } else {
        // Convert raw stored markdown directly to HTML/plain text.
        // We intentionally bypass BlockNote editor instances here because the
        // editor's block model has normalizeMarkdownNewlines() baked in, which
        // promotes single newlines to double newlines. Using the raw stored
        // content preserves the original line spacing on paste.
        try {
          const sectionHtml = await marked(currentText);
          htmlString += sectionHtml;
          plainTextString +=
            removeMd(currentText, {
              stripListLeaders: false,
              useImgAltText: false,
            }) + '\n\n';
        } catch (e) {
          console.error('Error parsing markdown:', e);
          const textWithBreaks = currentText.replace(/\n/g, '<br/>');
          htmlString += `<div style="white-space: pre-wrap;">${textWithBreaks}</div>`;
          plainTextString += currentText + '\n\n';
        }
      }

      // Copy to clipboard
      const success = await copyRichText(htmlString, plainTextString, includeHtml);

      if (success) {
        setCopyState('success');
        enqueueSnackbar('Section copied to clipboard!', {
          variant: 'success',
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        });

        // Track copy event (existing telemetry)
        noteSectionMetrics.copied({
          section_id: section.section_id,
          section_type: section.name,
          copy_method: 'button',
          include_formatting: includeHtml,
        });

        // Track copy event (new telemetry model)
        if (noteId) {
          const context: TelemetryContext = {
            docId: `note:${noteId}:section:${section.section_id}`,
            getDocVersion: () => '0',
          };

          buildSelectionCopied({
            context,
            lenSelected: currentText.length,
            lenTotal: currentText.length,
            modality: 'copy_button',
            metadata: {
              note_id: noteId,
              section_id: section.section_id,
              module_id: section.module_id ?? undefined,
              section_title: section.name,
              encounter_id: encounterId,
            },
          });
        }

        // Reset to idle after 2 seconds
        clearStateTimeout();
        timeoutRef.current = setTimeout(() => {
          setCopyState('idle');
          timeoutRef.current = null;
        }, 2000);
      } else {
        setCopyState('error');
        enqueueSnackbar('Failed to copy section', { variant: 'error' });
        clearStateTimeout();
        timeoutRef.current = setTimeout(() => {
          setCopyState('idle');
          timeoutRef.current = null;
        }, 2000);
      }
    } catch (error) {
      console.error('Error copying section:', error);
      setCopyState('error');
      enqueueSnackbar('Failed to copy section', { variant: 'error' });
      clearStateTimeout();
      timeoutRef.current = setTimeout(() => {
        setCopyState('idle');
        timeoutRef.current = null;
      }, 2000);
    }
  };

  return {
    copySection,
    copyState,
  };
}
